/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { usageCreateStripeCheckout } from "../funcs/usageCreateStripeCheckout.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { StripeEmbeddedCheckout } from "../models/components/stripeembeddedcheckout.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateStripeCheckoutRequest,
  CreateStripeCheckoutSecurity,
} from "../models/operations/createstripecheckout.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateStripeCheckoutMutationVariables = {
  request?: CreateStripeCheckoutRequest | undefined;
  security?: CreateStripeCheckoutSecurity | undefined;
  options?: RequestOptions;
};

export type CreateStripeCheckoutMutationData = StripeEmbeddedCheckout;

export type CreateStripeCheckoutMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * createStripeCheckout usage
 *
 * @remarks
 * Create an embedded Stripe Checkout session for starting PAYG billing
 */
export function useCreateStripeCheckoutMutation(
  options?: MutationHookOptions<
    CreateStripeCheckoutMutationData,
    CreateStripeCheckoutMutationError,
    CreateStripeCheckoutMutationVariables
  >,
): UseMutationResult<
  CreateStripeCheckoutMutationData,
  CreateStripeCheckoutMutationError,
  CreateStripeCheckoutMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateStripeCheckoutMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateStripeCheckout(): MutationKey {
  return ["@gram/client", "usage", "createStripeCheckout"];
}

export function buildCreateStripeCheckoutMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateStripeCheckoutMutationVariables,
  ) => Promise<CreateStripeCheckoutMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateStripeCheckout(),
    mutationFn: function createStripeCheckoutMutationFn({
      request,
      security,
      options,
    }): Promise<CreateStripeCheckoutMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(usageCreateStripeCheckout(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
